import { useForm } from "react-hook-form";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {useRecoilState} from "recoil";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import FileUploader from "../../../../../util/component/FileUploader";
import CloseIcon from "@mui/icons-material/Close";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { WarningRounded } from "@mui/icons-material";
import { useReportRepository } from "../../../api/repositories/ReportRepository";
import { STATE_REPORT_UPDATING } from "../constants/EnelxRecoil";

interface FuelModalInputs {
    supplier: string,
    legalEntity: string,
    dealer: string
}

const ReportModal = (props: any) => {
    const {t} = useTranslation('finance', {keyPrefix: 'enelx'});
    const { t: tg } = useTranslation("translation");
    const {uploadReportWithLegalEntityAndDealer} = useReportRepository();
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const {dispatchGlobalData} = useContext(GlobalContext);
    // Use Context
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
    }));

    const [dealers, setDealers] = useState<any[]>([]);

    const schema = Yup.object().shape({
        supplier: Yup.string().required(t('modal.messages.requiredCheck')),
        legalEntity: Yup.string().required(t('modal.messages.requiredCheck')),
        dealer: Yup.string().required(t('modal.messages.requiredCheck')),
    });
    const [errorMessageBox, showErrorMessageBox] = useState<boolean>(false);
    const defaultValues = () => {
        return {
            supplier: "",
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: ""
        }
    };

    const {watch, control, reset} = useForm<FuelModalInputs>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues()
    });



    const onSubmit = (file: File) => {
        let fileExtension = file.name.split('.').pop();
        if (fileExtension!.toLowerCase() === "xlsx" || fileExtension!.toLowerCase() === "xls") {
            uploadReportWithLegalEntityAndDealer(
                file,
                watch("legalEntity"),
                watch("dealer")
            ).then((response) => {
                if(response.status && response.status === "OK") {
                    props.onClose();
                    setUpdating(true);
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("modal.messages.fileUploaded"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    reset(defaultValues());
                } else {                    
                    let message = t('import.error-dialog.errors.generic');
                    
                    if (response.message) {
                        if(response.message.startsWith("FieldValueParsingException")) {
                            message =  t("import.error-dialog.errors.import."+response.message.split("|")[0],
                                {column : response.message.split("|")[1],
                                    line: response.message.split("|")[2]});
                        }  else if(response.message.includes("errorFileStructure")){
                            message =  t("import.error-dialog.errors."+response.message.split("|")[0],
                                {column : response.message.split("|")[1]});
                        }
                    } 
             
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message,
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });

                    props.onClose();
                    reset(defaultValues());
                }
            });
        } else {
            showErrorMessageBox(true);
            props.onClose();
            setUpdating(true);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("modal.handleErrors.wrongFileFormat"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            reset(defaultValues());
        }

    }

    const handleChangeLegalEntities = (value: any) => {
        console.log("handleChangeLegalEntities", value)
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
            return {
                label: tg(`dealers.${x.name}`),
                value: x.name,
            };
        });
        setDealers(dealers);
    }

    useEffect(() => {
        if (legalEntityOptions.length == 1 && dealers.length == 0) {
            setTimeout(() => {
                handleChangeLegalEntities(legalEntityOptions[0].value)
            });
        } 
    })

    return (
        <Dialog fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('modal.title')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                    <Grid item xs={6}>
                        <FormSelect
                            name={"legalEntity"}
                            control={control}
                            props={{
                                label: tg("shared.legalEntities"),
                                fullWidth: true,
                                required: true,
                                onChange: (e: any) => {
                                    handleChangeLegalEntities(e.target.value)
                                },
                            }}
                            items={legalEntityOptions.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />                       
                    </Grid>
                    <Grid item xs={6}>
                    <FormSelect
                            name={"dealer"}
                            control={control}
                            props={{
                                label: tg("shared.dealers"),
                                fullWidth: true,
                                required: true,
                                disabled: dealers.length == 0
                            }}
                            items={dealers.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />
                   </Grid>
                    <Grid item xs={12}>
                        <FileUploader
                            disabled={watch("legalEntity") === "" || watch("dealer") === ""}
                            errorMessage={t('modal.handleErrors.wrongFileFormat')}
                            onUpload={(file: File) => {
                                onSubmit(file)
                            }}
                            acceptedFiles={{'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls', '.xlsx']}}/>
                    </Grid>
                    {errorMessageBox &&
                        <Grid item xs={12}>
                            <Box style={{display: 'flex', gap: '1ch'}}>
                                <WarningRounded/>
                                <TextField
                                    color={"error"}
                                    fullWidth
                                    id="error"
                                    disabled={true}
                                    value={t("handleErrors.wrongFileFormat")}
                                    type="text"
                                    multiline
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />
                            </Box>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "10px"}}>
                <Button color={"accent"}
                        onClick={() => {
                            reset(defaultValues());
                            props.onClose();
                        }}>{t('modal.buttons.close')}</Button>
            </DialogActions>

        </Dialog>
    )
}

export default ReportModal;
